/**
 * Clock Page
 * Clock in/out interface with camera and GPS
 */

import { useState } from 'react'
import { Clock, CheckCircle, AlertCircle, LogIn, LogOut } from 'lucide-react'
import { Button, Card, Badge } from '../ui'
import { useGeolocation } from '../../hooks'
import { useAppStore } from '../../stores'
import { formatTime, formatDuration } from '../../utils/helpers'

// =============================================================================
// COMPONENT
// =============================================================================

export function ClockPage() {
  const { addClockEntry, setLoading, isLoading } = useAppStore()
  const getTodayClockStatus = useAppStore((state) => state.getTodayClockStatus)
  const clockStatus = getTodayClockStatus()
  
  const { location } = useGeolocation()
  
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  
  const handleClockAction = async (type: 'clock-in' | 'clock-out') => {
    try {
      setError(null)
      
      // Show confirmation dialog for clock out
      if (type === 'clock-out') {
        const confirmed = window.confirm('Are you sure you want to clock out?')
        if (!confirmed) {
          return
        }
      }
      
      setLoading(true)
      
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))
      
      // Add clock entry to store
      addClockEntry({
        type,
        timestamp: new Date().toISOString(),
        photo: '', // No photo required
        location: location || {
          latitude: 0,
          longitude: 0,
          accuracy: 0,
          timestamp: new Date().toISOString(),
        },
        status: 'pending',
      })
      
      // Reset and show success
      setSuccess(type === 'clock-in' ? 'Clocked in successfully!' : 'Clocked out successfully!')
      
      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch {
      setError(`Failed to ${type === 'clock-in' ? 'clock in' : 'clock out'}. Please try again.`)
    } finally {
      setLoading(false)
    }
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-black rounded-lg">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-medium text-gray-900">Time Clock</h1>
            <p className="text-sm text-gray-500">Record your work hours</p>
          </div>
        </div>
      </div>
      
      {/* Status Card */}
      <Card className={clockStatus.clockedIn ? 'bg-green-50 border-green-200' : ''}>
        <div className="text-center py-4">
          <Badge
            variant={clockStatus.clockedIn ? 'success' : 'secondary'}
            size="lg"
          >
            {clockStatus.clockedIn ? 'Clocked In' : 'Clocked Out'}
          </Badge>
          
          {clockStatus.clockInTime && (
            <div className="mt-4 space-y-1">
              <p className="text-sm text-gray-500">
                Clock In: <span className="font-medium text-gray-900">{formatTime(clockStatus.clockInTime)}</span>
              </p>
              {clockStatus.clockOutTime && (
                <p className="text-sm text-gray-500">
                  Clock Out: <span className="font-medium text-gray-900">{formatTime(clockStatus.clockOutTime)}</span>
                </p>
              )}
              {clockStatus.totalHours !== null && (
                <p className="text-sm text-gray-500 mt-2">
                  Total: <span className="font-medium text-gray-900">{formatDuration(clockStatus.totalHours)}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
      
      {/* Success Message */}
      {success && (
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-700 font-medium">{success}</p>
          </div>
        </Card>
      )}
      
      {/* Error Message */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        </Card>
      )}
      
      {/* Action Buttons - Always show unless it's a completed full shift */}
      <div className="space-y-3">
        {!clockStatus.clockedIn ? (
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={() => handleClockAction('clock-in')}
            loading={isLoading}
          >
            <LogIn className="w-5 h-5 mr-2" />
            Clock In
          </Button>
        ) : (
          <Button
            variant="danger"
            size="lg"
            fullWidth
            onClick={() => handleClockAction('clock-out')}
            loading={isLoading}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Clock Out
          </Button>
        )}
      </div>
    </div>
  )
}
